import { stableRecordKey } from '../lib/rowIdentity';
import React from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { MandalaCorner } from '../components/luxury/MandalaCorner';
import { useState, useEffect } from 'react';
import { api } from '../App';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};


function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-[hsl(var(--lux-gold)/0.3)] rounded-[var(--lux-radius-card)] overflow-hidden transition-colors hover:border-[hsl(var(--lux-gold)/0.6)]" data-testid="faq-item">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-[hsl(var(--lux-royal-soft))] transition-colors min-h-[44px]"
        data-testid="faq-question"
      >
        <span className="font-semibold text-[hsl(var(--lux-royal))]" style={{ fontFamily: 'Cormorant Garamond' }}>
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-[hsl(var(--lux-gold))] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="p-4 pt-0 text-[hsl(var(--lux-ink)/0.8)] leading-relaxed whitespace-pre-line" data-testid="faq-answer">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function LuxuryFAQ() {
  const [FAQS, setFaqs] = useState(null);
  useEffect(() => { api.get('/api/faqs').then(d => setFaqs(d.sections || [])).catch(() => setFaqs([])); }, []);
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" />
        <MandalaCorner position="bottom-right" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <HelpCircle className="w-16 h-16 text-[hsl(var(--lux-gold))] mx-auto mb-6" />
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>
              Frequently Asked <span className="text-[hsl(var(--lux-gold))]">Questions</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto">
              Find answers to common questions about membership, events, and donations
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12" data-testid="faq-sections">
          {FAQS === null && [0, 1, 2].map(i => <div key={i} className="h-24 rounded-[var(--lux-radius-card)] bg-white/60 animate-pulse" />)}
          {FAQS && FAQS.length === 0 && <p className="text-center text-[hsl(var(--lux-ink)/0.7)]" data-testid="faq-empty">No questions have been published yet — please use the contact form below.</p>}
          {(FAQS || []).map((section, idx) => (
            <motion.div key={stableRecordKey(section)} {...fadeInUp} transition={{ delay: idx * 0.1 }}>
              <h2 className="text-3xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>
                {section.category}
              </h2>
              <div className="space-y-3">
                {section.questions.map((faq, fIdx) => (
                  <FAQItem key={stableRecordKey(faq)} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))]">
              <CardContent className="p-8 text-center">
                <HelpCircle className="w-12 h-12 text-[hsl(var(--lux-gold))] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>
                  Still Have Questions?
                </h3>
                <p className="text-[hsl(var(--lux-ink)/0.7)] mb-6">
                  Can't find what you're looking for? Get in touch with our team.
                </p>
                <a href="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-[hsl(var(--lux-royal))] text-white rounded-lg hover:bg-[hsl(var(--lux-royal-2))] transition-colors font-semibold">
                  Contact Us
                </a>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
